import {
  PERIODIC_BADGE_SYNC_MIN_INTERVAL_MS,
  PERIODIC_BADGE_SYNC_TAG,
} from '@module/mdrt-org/shared/utils/constants/notification-badge';

type PeriodicSyncManager = {
  getTags: () => Promise<string[]>;
  register: (tag: string, options?: { minInterval: number }) => Promise<void>;
  unregister: (tag: string) => Promise<void>;
};

type ServiceWorkerRegistrationWithPeriodicSync = ServiceWorkerRegistration & {
  periodicSync?: PeriodicSyncManager;
};

// Registers Periodic Background Sync to refresh the unread-notifications app icon
// badge while the app is closed. Chromium-only and a progressive enhancement —
// unsupported platforms (e.g. iOS) simply skip it and rely on push + foreground
// refresh. All failures are swallowed.
export const registerPeriodicBadgeSync = async (
  registration: ServiceWorkerRegistration
): Promise<void> => {
  const periodicSync = (registration as ServiceWorkerRegistrationWithPeriodicSync).periodicSync;

  if (!periodicSync) return;

  try {
    const status = await navigator.permissions.query({
      name: 'periodic-background-sync' as PermissionName,
    });

    if (status.state !== 'granted') return;

    const existingTags = await periodicSync.getTags();

    if (existingTags.includes(PERIODIC_BADGE_SYNC_TAG)) return;

    await periodicSync.register(PERIODIC_BADGE_SYNC_TAG, {
      minInterval: PERIODIC_BADGE_SYNC_MIN_INTERVAL_MS,
    });
  } catch {
    // best-effort — periodic sync is a progressive enhancement
  }
};

// Removes the periodic badge sync registration. Used when the badge feature flag
// is disabled so the browser stops waking the Service Worker to refresh the badge.
// Best-effort and Chromium-only — silently no-ops elsewhere.
export const unregisterPeriodicBadgeSync = async (
  registration: ServiceWorkerRegistration
): Promise<void> => {
  const periodicSync = (registration as ServiceWorkerRegistrationWithPeriodicSync).periodicSync;

  if (!periodicSync) return;

  try {
    const existingTags = await periodicSync.getTags();

    if (!existingTags.includes(PERIODIC_BADGE_SYNC_TAG)) return;

    await periodicSync.unregister(PERIODIC_BADGE_SYNC_TAG);
  } catch {
    // best-effort — periodic sync is a progressive enhancement
  }
};
